import { useEffect, useState } from "react"
import { addPageAction } from "../../../utils/newrelic"
import { MessagingIconFilled } from "../../common/atoms/Icons/Chat"
import { UnreadBadge } from "../common/UnreadBadge/UnreadBadge"
import { useUnreadCount } from "../hooks/useUnreadCount"
import { classNames } from "../utils"
import styles from "./MessagingEntrypoint.module.scss"
import type { ConversationListData, DMUnreadData, EventRouter } from "../types"

type Props = {
    dmUnreadData: DMUnreadData
    conversationListData: ConversationListData
    openMessagingInbox: EventRouter<void>
    onInboxClosed?: EventRouter<void>
}

const UNREAD_BADGE_MAX_COUNT = 99

export function MessagingEntrypoint({
    dmUnreadData,
    conversationListData,
    openMessagingInbox,
    onInboxClosed,
}: Props) {
    const [isVisible, setIsVisible] = useState(true)
    const { unreadCount } = useUnreadCount({
        dmUnreadData,
        conversationListData,
    })
    const hasUnread = unreadCount > 0

    useEffect(() => {
        if (onInboxClosed) {
            const listener = () => {
                setIsVisible(true)
            }
            onInboxClosed.listen(listener)
            return () => {
                onInboxClosed.removeListener(listener)
            }
        }
    }, [onInboxClosed, setIsVisible])

    const handleClick = () => {
        setIsVisible(false)
        openMessagingInbox.fire(undefined)
        addPageAction("DMListOpened", {
            source: "desktop",
            hasUnread: unreadCount > 0,
            inboxVersion: "v2",
        })
    }

    return (
        <div
            className={classNames(styles.messagingEntrypoint, {
                [styles.dismissed]: !isVisible,
            })}
            onClick={handleClick}
            data-testid="messaging-entrypoint"
        >
            <MessagingIconFilled width={18} height={18} />
            {hasUnread && (
                <UnreadBadge
                    count={unreadCount}
                    className={classNames(styles.unreadBadgeCustom, {
                        [styles.largeCount]:
                            unreadCount > UNREAD_BADGE_MAX_COUNT,
                    })}
                    maxCount={UNREAD_BADGE_MAX_COUNT}
                />
            )}
        </div>
    )
}
